import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Check, X, Phone, Mail, Save, History, ShieldAlert, Calendar, Loader2,
} from "lucide-react";
import { detalheMarcacao, alterarEstado, guardarNotas } from "../../lib/adminApi";
import { Card, EstadoBadge, MarcacaoLink, PageTitle, ClinicaLabel, formatDateLong, formatDate } from "../ui";

const ACOES = [
  { estado: "confirmada", label: "Confirmar", cls: "bg-sky-600 hover:bg-sky-700", icon: Check, testId: "btn-confirmar" },
  { estado: "concluida", label: "Marcar concluída", cls: "bg-emerald-600 hover:bg-emerald-700", icon: Check, testId: "btn-concluir" },
  { estado: "no_show", label: "Não compareceu", cls: "bg-rose-600 hover:bg-rose-700", icon: X, testId: "btn-noshow" },
  { estado: "cancelada", label: "Cancelar", cls: "bg-zinc-700 hover:bg-zinc-800", icon: X, testId: "btn-cancelar", askMotivo: true },
];

export default function AdminMarcacaoDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notas, setNotas] = useState("");
  const [salvandoNotas, setSalvandoNotas] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [motivoModal, setMotivoModal] = useState(null);
  const [motivoText, setMotivoText] = useState("");

  const carregar = async () => {
    setLoading(true);
    try {
      const r = await detalheMarcacao(id);
      setData(r);
      setNotas(r.marcacao?.notas_internas || "");
    } finally { setLoading(false); }
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [id]);

  const onAcao = async (estado, motivo) => {
    setActionLoading(estado);
    try {
      await alterarEstado(id, estado, motivo);
      await carregar();
    } finally {
      setActionLoading(null);
      setMotivoModal(null);
      setMotivoText("");
    }
  };

  const onSalvarNotas = async () => {
    setSalvandoNotas(true);
    try { await guardarNotas(id, notas); } finally { setSalvandoNotas(false); }
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-[#5A5A5A]" data-testid="loading"><Loader2 className="h-4 w-4 animate-spin"/>A carregar...</div>
  );
  if (!data) return <p>Não encontrado.</p>;

  const m = data.marcacao;
  const c = data.clinica;
  const s = data.servico;

  return (
    <div data-testid="admin-marcacao-detail">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-bold text-[#1E5F6E] mb-4">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </button>
      <div className="flex flex-wrap items-center gap-3 justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#88A096]">Marcação</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">{m.numero_marcacao}</h1>
        </div>
        <EstadoBadge estado={m.estado} />
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {ACOES.map((a) => (
          <button
            key={a.estado}
            disabled={actionLoading === a.estado || m.estado === a.estado}
            data-testid={a.testId}
            onClick={() => a.askMotivo ? setMotivoModal(a) : onAcao(a.estado)}
            className={`inline-flex items-center gap-2 rounded-full text-white px-4 py-2.5 text-sm font-bold transition-colors disabled:opacity-40 ${a.cls}`}
          >
            {actionLoading === a.estado ? <Loader2 className="h-4 w-4 animate-spin" /> : <a.icon className="h-4 w-4" />}
            {a.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2" testId="card-marcacao">
          <h2 className="font-display font-bold text-lg mb-3">Marcação</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase font-bold text-[#88A096] tracking-widest">Quando</div>
              <div className="mt-1 font-semibold">{formatDateLong(m.data_hora)}</div>
            </div>
            <div>
              <div className="text-xs uppercase font-bold text-[#88A096] tracking-widest">Clínica</div>
              <div className="mt-1 font-semibold">{c?.nome} — <ClinicaLabel slug={m.clinica_slug} /></div>
              <div className="text-xs text-[#5A5A5A]">{c?.morada}</div>
            </div>
            <div>
              <div className="text-xs uppercase font-bold text-[#88A096] tracking-widest">Serviço</div>
              <div className="mt-1 font-semibold">{s?.nome}</div>
              <div className="text-xs text-[#5A5A5A]">{m.duracao_minutos} min</div>
            </div>
            <div>
              <div className="text-xs uppercase font-bold text-[#88A096] tracking-widest">Criado</div>
              <div className="mt-1 font-semibold">{formatDate(m.created_at)}</div>
            </div>
          </div>

          {m.motivo && (
            <div className="mt-5 rounded-xl bg-[#FAF7F2] border border-[#1E5F6E]/10 p-4">
              <div className="text-xs uppercase font-bold text-[#88A096] tracking-widest mb-1">Motivo da consulta (cliente)</div>
              <p className="text-sm whitespace-pre-line">{m.motivo}</p>
            </div>
          )}
        </Card>

        <div className="space-y-5">
          <Card testId="card-tutor">
            <div className="text-xs uppercase font-bold text-[#88A096] tracking-widest mb-1">Tutor</div>
            <div className="font-display text-lg font-bold">{m.tutor.nome}</div>
            <div className="text-sm text-[#5A5A5A] mt-1">{m.tutor.email}</div>
            <div className="text-sm text-[#5A5A5A]">{m.tutor.telefone}</div>
            <div className="mt-3 flex gap-2">
              <a href={`tel:${m.tutor.telefone}`} data-testid="btn-call-tutor" className="inline-flex items-center gap-1.5 rounded-full bg-[#1E5F6E] text-white px-3 py-1.5 text-xs font-bold">
                <Phone className="h-3 w-3" /> Ligar
              </a>
              <a href={`mailto:${m.tutor.email}`} data-testid="btn-email-tutor" className="inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-[#1E5F6E]/15 text-[#1E5F6E] px-3 py-1.5 text-xs font-bold">
                <Mail className="h-3 w-3" /> Email
              </a>
            </div>
            {m.tutor.primeira_vez && (
              <div className="mt-3 text-[10px] uppercase tracking-widest font-bold text-[#E8A87C]">Primeira visita</div>
            )}
          </Card>

          <Card testId="card-animal">
            <div className="text-xs uppercase font-bold text-[#88A096] tracking-widest mb-1">Animal</div>
            <div className="font-display text-lg font-bold">{m.animal.nome}</div>
            <div className="text-sm text-[#5A5A5A] capitalize">{m.animal.especie}{m.animal.raca ? ` — ${m.animal.raca}` : ""}</div>
            {m.animal.idade_anos != null && (
              <div className="text-sm text-[#5A5A5A]">{m.animal.idade_anos} {Number(m.animal.idade_anos) === 1 ? "ano" : "anos"}</div>
            )}
            {m.animal.sexo && <div className="text-xs text-[#5A5A5A] capitalize">Sexo: {m.animal.sexo}</div>}
          </Card>
        </div>

        <Card className="lg:col-span-2" testId="card-notas">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-[#E8A87C]" />
              <h2 className="font-display font-bold text-lg">Notas internas</h2>
              <span className="text-[10px] uppercase tracking-widest text-[#E8A87C] font-bold">Apenas equipa</span>
            </div>
            <button onClick={onSalvarNotas} disabled={salvandoNotas} data-testid="btn-save-notas" className="inline-flex items-center gap-1.5 rounded-full bg-[#1E5F6E] text-white px-3.5 py-1.5 text-xs font-bold disabled:opacity-40">
              {salvandoNotas ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Guardar
            </button>
          </div>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            data-testid="input-notas"
            rows={5}
            maxLength={2000}
            className="w-full rounded-xl bg-white border-2 border-[#1E5F6E]/10 px-4 py-3 text-sm focus:border-[#1E5F6E] focus:outline-none"
            placeholder="Adicione notas internas sobre esta marcação..."
          />
        </Card>

        <Card testId="card-historico">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-[#1E5F6E]" />
            <h2 className="font-display font-bold text-lg">Histórico do cliente</h2>
          </div>
          {data.historico.length === 0 ? (
            <p className="text-sm text-[#5A5A5A]">Primeira marcação deste cliente.</p>
          ) : (
            <ul className="divide-y divide-[#1E5F6E]/10 text-sm">
              {data.historico.map((h) => (
                <li key={h.id} className="py-2.5">
                  <MarcacaoLink id={h.id}>{h.numero_marcacao}</MarcacaoLink> · {formatDate(h.data_hora)}
                  <div className="text-xs text-[#5A5A5A]">{h.estado} · <ClinicaLabel slug={h.clinica_slug} /></div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {motivoModal && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setMotivoModal(null)} data-testid="modal-motivo">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold mb-2">Cancelar marcação?</h3>
            <p className="text-sm text-[#5A5A5A] mb-4">Indique o motivo (opcional). Será enviado email ao tutor.</p>
            <textarea
              value={motivoText}
              onChange={(e) => setMotivoText(e.target.value)}
              data-testid="input-motivo"
              rows={3}
              className="w-full rounded-xl border-2 border-[#1E5F6E]/10 px-3 py-2 text-sm"
              placeholder="ex.: Médico indisponível"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setMotivoModal(null)} className="rounded-full bg-white border-2 border-[#1E5F6E]/15 px-4 py-2 text-sm font-bold text-[#1E5F6E]">Voltar</button>
              <button onClick={() => onAcao(motivoModal.estado, motivoText)} data-testid="btn-confirm-motivo" className="rounded-full bg-rose-600 text-white px-4 py-2 text-sm font-bold">Sim, cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
